import axios from "axios";
import { ListingFilters } from "@/lib/types";
import { API_BASE_URL } from "@/lib/api/config"; // Import API_BASE_URL from central config

// Create axios instance with dynamic base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Convert API parameter names to match what the backend expects
const convertParamsToApiFormat = (params: ListingFilters = {}) => {
  const apiParams: Record<string, any> = {};

  // Direct mappings (same name in frontend and API)
  const directMappings = [
    "page",
    "limit",
    "location",
    "propertyType",
    "listing_type",
    "search_term",
    "sort_by",
    "sort_order",
    "boosted",
    "status",
    "provinceIds", // Add provinceIds
    "districtIds", // Add districtIds
    "cityIds", // Add cityIds
  ];

  // Copy direct mappings
  directMappings.forEach((key) => {
    if (params[key as keyof ListingFilters] !== undefined) {
      const value = params[key as keyof ListingFilters];
      // Ensure arrays are stringified for URL params if not handled by axios
      if (Array.isArray(value)) {
        apiParams[key] = value.join(",");
      } else {
        apiParams[key] = value;
      }
    }
  });

  // Handle specific mappings
  if (params.minPrice !== undefined) {
    apiParams.min_price = params.minPrice; // API expects min_price
  }
  if (params.maxPrice !== undefined) {
    apiParams.max_price = params.maxPrice; // API expects max_price
  }
  if (params.bedrooms !== undefined) {
    apiParams.min_bedrooms = params.bedrooms; // API expects min_bedrooms
  }
  if (params.bathrooms !== undefined) {
    apiParams.min_bathrooms = params.bathrooms; // API expects min_bathrooms
  }
  if (params.minSize !== undefined) {
    // Add minSize
    apiParams.min_size = params.minSize; // API expects min_size
  }
  if (params.maxSize !== undefined) {
    // Add maxSize
    apiParams.max_size = params.maxSize; // API expects max_size
  }

  // Handle featured flag - API controller expects 'featured' param
  if (params.featured !== undefined) {
    apiParams.featured = params.featured; // Sends as boolean, axios should convert to "true"/"false"
  }

  return apiParams;
};

export const listingsApi = {
  // Get listings with filters
  getListings: async (params: ListingFilters = {}) => {
    try {
      const apiParams = convertParamsToApiFormat(params);

      const response = await api.get("/api/listings", { params: apiParams });

      return response.data;
    } catch (error) {
      console.error("Error fetching listings:", error);
      if (axios.isAxiosError(error)) {
        console.error("API error response:", error.response?.data);
      }
      throw error;
    }
  },

  // Get a single listing by ID
  getListing: async (id: string) => {
    try {
      const response = await api.get(`/api/listings/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching listing with ID ${id}:`, error);
      throw error;
    }
  },

  // Create a new listing
  createListing: async (listingData: any) => {
    try {
      const response = await api.post("/api/listings", listingData);
      return response.data;
    } catch (error) {
      console.error("Error creating listing:", error);
      throw error;
    }
  },

  // Update an existing listing
  updateListing: async (id: string, listingData: any) => {
    try {
      const response = await api.put(`/api/listings/${id}`, listingData);
      return response.data;
    } catch (error) {
      console.error(`Error updating listing with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete a listing
  deleteListing: async (id: string) => {
    try {
      const response = await api.delete(`/api/listings/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting listing with ID ${id}:`, error);
      throw error;
    }
  },

  // Get featured listings
  getFeaturedListings: async (limit: number = 3) => {
    try {
      const response = await api.get("/api/listings/featured", {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching featured listings:", error);
      if (axios.isAxiosError(error)) {
        console.error("API error response:", error.response?.data);
      }
      throw error;
    }
  },
};
